import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { BarChart3, Users, TrendingUp, DollarSign, FileText, BookOpen, Calendar, Eye, Loader2, MapPin } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { API } from '@/App';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const COLORS = ['#1e3a5f', '#35AFE4', '#27ae60', '#e67e22', '#e74c3c', '#8e44ad', '#2c3e50', '#16a085', '#f39c12', '#c0392b'];

const AnalyticsPage = () => {
  const [tab, setTab] = useState('members');
  const [members, setMembers] = useState(null);
  const [content, setContent] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/analytics/members`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/admin/analytics/content`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/admin/analytics/revenue`, { headers }).catch(() => ({ data: null })),
    ]).then(([m, c, r]) => {
      setMembers(m.data);
      setContent(c.data);
      setRevenue(r.data);
    }).finally(() => setLoading(false));
  }, []);

  const tabs = [
    { key: 'members', label: 'Membres', icon: Users },
    { key: 'content', label: 'Contenu', icon: FileText },
    { key: 'revenue', label: 'Cotisations', icon: DollarSign },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 size={32} className="animate-spin text-[#35AFE4]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-5 lg:p-8 min-h-full bg-[#f8f9fb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="analytics-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>Analytics avancés</h1>
            <p className="text-sm text-gray-400 mt-1">Tableaux de bord détaillés</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#eaf6fc] flex items-center justify-center">
            <BarChart3 size={18} className="text-[#35AFE4]" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-xl border border-gray-100 p-1 w-fit" data-testid="analytics-tabs">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === t.key ? 'bg-[#1e3a5f] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                data-testid={`tab-${t.key}`}>
                <Icon size={14} />{t.label}
              </button>
            );
          })}
        </div>

        {/* Members Tab */}
        {tab === 'members' && members && (
          <div className="space-y-5" data-testid="members-analytics">
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard label="Total membres" value={members.total_members} icon={Users} color="bg-blue-100 text-blue-600" />
              <KpiCard label="Actifs" value={members.active_members} icon={TrendingUp} color="bg-emerald-100 text-emerald-600" />
              <KpiCard label="Taux d'activation" value={`${members.total_members > 0 ? Math.round((members.active_members / members.total_members) * 100) : 0}%`} icon={BarChart3} color="bg-violet-100 text-violet-600" />
              <KpiCard label="Régions couvertes" value={members.regions?.length || 0} icon={MapPin} color="bg-amber-100 text-amber-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Member growth chart */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="growth-chart">
                <p className="text-sm font-bold text-gray-900 mb-1">Croissance des membres</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-4">{members.year}</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={members.growth.map((g, i) => ({ name: MONTHS[i], value: g.new_members }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '12px' }} />
                    <Bar dataKey="value" fill="#1e3a5f" radius={[6, 6, 0, 0]} name="Nouveaux membres" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Role distribution */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="role-distribution">
                <p className="text-sm font-bold text-gray-900 mb-4">Répartition par rôle</p>
                {members.roles?.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={members.roles} dataKey="count" nameKey="role" cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4}>
                          {members.roles.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2 mt-2">
                      {members.roles.map((r, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="text-xs font-medium text-gray-700">{r.role}</span>
                          </div>
                          <span className="text-xs font-bold text-gray-900">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <EmptyState label="Aucune donnée de rôles" />}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Subscription status */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="subscription-status">
                <p className="text-sm font-bold text-gray-900 mb-4">Statut des adhésions</p>
                {members.subscriptions?.length > 0 ? (
                  <div className="space-y-3">
                    {members.subscriptions.map((s, i) => {
                      const pct = members.total_members > 0 ? Math.round((s.count / members.total_members) * 100) : 0;
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-700">{s.status}</span>
                            <span className="text-xs font-bold text-gray-900">{s.count} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <EmptyState label="Aucune donnée" />}
              </div>

              {/* Region distribution */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="region-distribution">
                <p className="text-sm font-bold text-gray-900 mb-4">Répartition par région</p>
                {members.regions?.length > 0 ? (
                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                    {members.regions.map((r, i) => {
                      const maxCount = members.regions[0]?.count || 1;
                      const pct = Math.round((r.count / maxCount) * 100);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs font-medium text-gray-600 w-32 truncate">{r.region}</span>
                          <div className="flex-1 h-5 bg-gray-50 rounded-lg overflow-hidden relative">
                            <div className="h-full rounded-lg" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                          <span className="text-xs font-bold text-gray-800 w-8 text-right">{r.count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : <EmptyState label="Aucune donnée régionale" />}
              </div>
            </div>
          </div>
        )}

        {/* Content Tab */}
        {tab === 'content' && content && (
          <div className="space-y-5" data-testid="content-analytics">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard label="Formations" value={content.counts?.formations || 0} icon={BookOpen} color="bg-violet-100 text-violet-600" />
              <KpiCard label="Articles" value={content.counts?.articles || 0} icon={FileText} color="bg-emerald-100 text-emerald-600" />
              <KpiCard label="Blog" value={content.counts?.blog || 0} icon={FileText} color="bg-blue-100 text-blue-600" />
              <KpiCard label="Événements" value={content.counts?.events || 0} icon={Calendar} color="bg-amber-100 text-amber-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <ContentList title="Formations populaires" items={content.top_formations} />
              <ContentList title="Articles populaires" items={content.top_articles} />
              <ContentList title="Blog populaires" items={content.top_blog} />
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {tab === 'revenue' && revenue && (
          <div className="space-y-5" data-testid="revenue-analytics">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard label={`Total ${revenue.current_year?.year}`} value={`${revenue.current_year?.total || 0}€`} icon={DollarSign} color="bg-amber-100 text-amber-600" />
              <KpiCard label={`Total ${revenue.previous_year?.year}`} value={`${revenue.previous_year?.total || 0}€`} icon={DollarSign} color="bg-gray-100 text-gray-600" />
              <KpiCard label="HelloAsso paiements" value={revenue.helloasso?.payments_count || 0} icon={TrendingUp} color="bg-emerald-100 text-emerald-600" />
              <KpiCard label="HelloAsso total" value={`${revenue.helloasso?.total_amount || 0}€`} icon={DollarSign} color="bg-blue-100 text-blue-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Revenue trend */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="revenue-trend">
                <p className="text-sm font-bold text-gray-900 mb-1">Tendance des cotisations</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-4">{revenue.current_year?.year} vs {revenue.previous_year?.year}</p>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={MONTHS.map((m, i) => ({
                    name: m,
                    current: revenue.current_year?.monthly?.[i]?.amount || 0,
                    previous: revenue.previous_year?.monthly?.[i]?.amount || 0,
                  }))}>
                    <defs>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}€`} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '12px' }} formatter={v => [`${v}€`, '']} />
                    <Area type="monotone" dataKey="previous" stroke="#d1d5db" fill="transparent" strokeWidth={1.5} strokeDasharray="4 4" name={`${revenue.previous_year?.year}`} />
                    <Area type="monotone" dataKey="current" stroke="#f59e0b" fill="url(#colorRev)" strokeWidth={2} name={`${revenue.current_year?.year}`} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Payment count per month */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="payment-count">
                <p className="text-sm font-bold text-gray-900 mb-1">Nombre de paiements par mois</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-4">{revenue.current_year?.year}</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={MONTHS.map((m, i) => ({
                    name: m,
                    count: revenue.current_year?.monthly?.[i]?.count || 0,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '12px' }} />
                    <Bar dataKey="count" fill="#35AFE4" radius={[6, 6, 0, 0]} name="Paiements" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const KpiCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 lg:p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center mb-3`}>
      <Icon size={16} />
    </div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="text-center py-8 text-gray-400">
    <BarChart3 size={24} className="mx-auto mb-2 opacity-30" />
    <p className="text-xs">{label}</p>
  </div>
);

const ContentList = ({ title, items }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`content-list-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-4">
      <Eye size={14} className="text-[#35AFE4]" />
      <p className="text-sm font-bold text-gray-900">{title}</p>
    </div>
    {items?.length > 0 ? (
      <div className="space-y-2">
        {items.map((item, i) => {
          const maxViews = items[0]?.views || 1;
          const pct = Math.max(5, (item.views / maxViews) * 100);
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="text-[10px] font-bold text-gray-400 w-4 text-right">{i + 1}</span>
              <div className="flex-1 relative">
                <div className="h-7 bg-gray-50 rounded-lg overflow-hidden">
                  <div className="h-full rounded-lg" style={{ width: `${pct}%`, backgroundColor: i < 3 ? '#1e3a5f' : '#e5e7eb' }} />
                </div>
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold truncate pr-8" style={{ color: i < 3 ? '#fff' : '#374151', maxWidth: '80%' }}>
                  {item.title || 'Sans titre'}
                </span>
              </div>
              <span className="text-[11px] font-bold text-gray-600 w-12 text-right flex-shrink-0">{item.views || 0}</span>
            </div>
          );
        })}
      </div>
    ) : <EmptyState label="Aucun contenu" />}
  </div>
);

export default AnalyticsPage;
